import { jsx } from "react/jsx-runtime";
import { useEffect, Suspense } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { Navigate } from "react-router-dom";
import { shuftiProVerifyToken } from "@nxplatform/geo-data/slices/ip-data.slice";
import { isSpa, getCmsRoutes } from "@nxplatform/helpers/common/common";
import { getBaseLang } from "@nxplatform/helpers/helpers-pure";
import { DisabledStatus } from "@nxplatform/platform-entities";
import { allowedLinks, parseLink } from "@nxplatform/platform-settings/locale-setup.routes";
import { selectShowHorseRacing } from "@nxplatform/platform-settings/locale-setup.selectors";
import { selectAsiaSkinEnabled, selectPlatformSettingsLiveCasinoEnabled, selectPlatformSettingsCasinoEnabled, selectPlatformSettingsIsFrontRenderText, selectPlatformSettingsIsText } from "@nxplatform/platform-settings/platform-settings.selectors";
import { selectCustomerEntities, selectIsGviveMigrationInProgress, selectIsOnDatoMigrationInProgress, selectCustomerTokenEntities } from "@nxplatform/store-shared/customer/shared-customer-selectors";
import { _Root } from "../../root.js";
import { GuardedRoute } from "../guarded-route.js";
import { useCreateCabinetRoutes } from "./cabinet-routes.hook.js";
import { useCreateCasinoRoutes } from "./casino-routes.hook.js";
import { guardedRoutesHook } from "./guarded-routes.hook.js";
import { useCreateOuterRoutes } from "./outer-routes.hook.js";
import { useCreateSportRoutes } from "./sport-routes.hook.js";
const useCeateRoutes = (templates, Container, additional, RootElement) => {
  const shuftiProToken = new URLSearchParams(window.location.search).get("shuftiProToken");
  const Root = RootElement || _Root;
  const dispatch = useDispatch();
  const verifyShuftiProToken = (token) => {
    dispatch(shuftiProVerifyToken(token));
  };
  const {
    ontarioMigrationInProgress,
    gviveMigrationInProgress,
    isTextPage,
    isFrontRenderTextPage,
    customer,
    horseRacingEnabled,
    asiaEnabled,
    isAuth
  } = useSelector((state) => ({
    ontarioMigrationInProgress: selectIsOnDatoMigrationInProgress(state),
    gviveMigrationInProgress: selectIsGviveMigrationInProgress(state),
    isTextPage: selectPlatformSettingsIsText(state),
    isFrontRenderTextPage: selectPlatformSettingsIsFrontRenderText(state),
    casino: selectPlatformSettingsCasinoEnabled(state),
    liveCasino: selectPlatformSettingsLiveCasinoEnabled(state),
    customer: selectCustomerEntities(state),
    horseRacingEnabled: selectShowHorseRacing(state),
    asiaEnabled: selectAsiaSkinEnabled(state),
    isAuth: !!selectCustomerTokenEntities(state)
  }), shallowEqual);
  const isBlocked = customer.disabledStatusCode === DisabledStatus.block_id_upload && !!(templates == null ? void 0 : templates.blockedCustomerPage);
  const isOntarioMigrationInProgress = ontarioMigrationInProgress && !!(templates == null ? void 0 : templates.blockedMigratingCustomer);
  const isGviveMigrationInProgress = gviveMigrationInProgress && !!(templates == null ? void 0 : templates.blockedMigratingCustomerGvive);
  const spa = isSpa();
  const cmsRoutes = getCmsRoutes();
  const {
    mainPage: MainPage,
    cabinet,
    sport,
    outerPages,
    casino,
    textFrontRender: TextFrontRender,
    eParkstsAuth: EParakstsAuth,
    blockedMigratingCustomer: BlockedMigrationCustomer,
    blockedMigratingCustomerGvive: BlockedMigratingCustomerGvive,
    blockedCustomerPage: BlockedCustomerPage,
    horseRacing: HorseRacing,
    sportPage: SportPage,
    text: Text,
    cabinetContainer,
    casinoContainer,
    outerContainer,
    sportContainer
  } = templates || {};
  const {
    sport: sportAdditional = [],
    outer: outerAdditional = [],
    casino: casinoAdditional = [],
    cabinet: cabinetAdditional = []
  } = additional || {};
  useEffect(() => {
    if (shuftiProToken) {
      verifyShuftiProToken(shuftiProToken);
    }
  }, [shuftiProToken]);
  const sportRoutes = useCreateSportRoutes(sport, sportContainer, sportAdditional);
  const cabinetRoutes = useCreateCabinetRoutes(cabinet, cabinetContainer, cabinetAdditional);
  const casinoRoutes = useCreateCasinoRoutes(casino, casinoContainer, casinoAdditional);
  const outerRoutes = useCreateOuterRoutes(outerPages, outerContainer, outerAdditional);
  return [{
    element: /* @__PURE__ */ jsx(Suspense, { children: /* @__PURE__ */ jsx(Root, { Container }) }),
    children: [
      isTextPage && !isFrontRenderTextPage && {
        path: "*",
        element: /* @__PURE__ */ jsx(
          GuardedRoute,
          {
            isBlocked,
            isGviveMigrationInProgress,
            children: /* @__PURE__ */ jsx(Text, {})
          }
        )
      },
      // вынести в blocked-routes.hook.tsx
      isOntarioMigrationInProgress && {
        path: allowedLinks.registrationMigration,
        element: /* @__PURE__ */ jsx(BlockedMigrationCustomer, {})
      },
      isGviveMigrationInProgress && {
        path: allowedLinks.registrationMigrationGvive,
        element: /* @__PURE__ */ jsx(BlockedMigratingCustomerGvive, {})
      },
      isBlocked && {
        // mb this should be in cabinet routes
        path: allowedLinks.blockedVerification,
        element: /* @__PURE__ */ jsx(BlockedCustomerPage, {})
      },
      EParakstsAuth && {
        path: allowedLinks.eParkstsAuth,
        element: /* @__PURE__ */ jsx(
          GuardedRoute,
          {
            isBlocked,
            isGviveMigrationInProgress,
            children: /* @__PURE__ */ jsx(EParakstsAuth, {})
          }
        )
      },
      horseRacingEnabled && HorseRacing && {
        path: allowedLinks.horseRacing,
        element: /* @__PURE__ */ jsx(
          GuardedRoute,
          {
            isBlocked,
            isGviveMigrationInProgress,
            children: /* @__PURE__ */ jsx(HorseRacing, {})
          }
        )
      },
      asiaEnabled && {
        path: allowedLinks.sportPage,
        element: /* @__PURE__ */ jsx(
          GuardedRoute,
          {
            isBlocked,
            isGviveMigrationInProgress,
            children: /* @__PURE__ */ jsx(SportPage, {})
          }
        )
      },
      {
        path: allowedLinks.home,
        element: /* @__PURE__ */ jsx(
          GuardedRoute,
          {
            isBlocked,
            isGviveMigrationInProgress,
            children: /* @__PURE__ */ jsx(MainPage, {})
          }
        )
      },
      ...guardedRoutesHook(cabinetRoutes, { isBlocked, isGviveMigrationInProgress }),
      ...guardedRoutesHook(casinoRoutes, { isBlocked, isGviveMigrationInProgress }),
      ...guardedRoutesHook(outerRoutes, { isBlocked, isGviveMigrationInProgress }),
      ...spa ? cmsRoutes.map((route) => ({
        key: route.path,
        path: parseLink(route.path, getBaseLang()),
        element: /* @__PURE__ */ jsx(
          GuardedRoute,
          {
            isBlocked,
            isGviveMigrationInProgress,
            children: /* @__PURE__ */ jsx(TextFrontRender, {})
          }
        )
      })) : [],
      ...sport ? guardedRoutesHook(sportRoutes, { isBlocked, isGviveMigrationInProgress }) : [],
      { path: "*", element: /* @__PURE__ */ jsx(Navigate, { to: allowedLinks.home, replace: true }) }
    ].filter(Boolean)
  }];
};
export {
  useCeateRoutes
};
